import { useEffect, useState } from "react";
import { useApp } from "../../../../../context/AppContext";
import { ServicePackage } from "../../../../../types/packages";
import PackageSpecialPicker from "../PackageSpecialPicker";

interface AddPackageLineItemProps {
  /** Salon UUID whose packages/specials are offered. */
  salonId: string;
  /** Salon IANA timezone — specials are filtered to their active window. */
  timezone?: string | null;
  /** Packages/specials pre-fetched by the parent modal. When provided the
   *  picker uses them and skips its own fetch. */
  packages?: ServicePackage[];
  /** Emits the add-line-item payload for the selected package. */
  onSubmit: (lineItemData: {
    service_packages: { service_package_id: string }[];
  }) => void;
}

/**
 * Renders the shared picker so a package can be sold onto the appointment's
 * transaction (a service_package line item). Uses the parent-supplied
 * `packages` when given (no refetch), otherwise loads the salon's list itself.
 * Selecting one and submitting emits
 * `{ service_packages: [{ service_package_id }] }`, which
 * TransactionDetailModal posts to `addLineItemToTransaction`.
 */
export default function AddPackageLineItem({
  salonId,
  timezone,
  packages: seededPackages,
  onSubmit,
}: AddPackageLineItemProps) {
  const { getServicePackages } = useApp();
  const [packages, setPackages] = useState<ServicePackage[]>(
    seededPackages ?? [],
  );
  const [isLoading, setIsLoading] = useState(seededPackages == null);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Parent supplied the list — use it and skip the fetch.
    if (seededPackages != null) {
      setPackages(seededPackages);
      setIsLoading(false);
      return;
    }
    let active = true;
    const load = async () => {
      try {
        setIsLoading(true);
        const list = await getServicePackages(salonId);
        if (active) setPackages(list ?? []);
      } catch (e) {
        console.error("Failed to load packages:", e);
        if (active) setError("Failed to load packages. Please try again.");
      } finally {
        if (active) setIsLoading(false);
      }
    };
    if (salonId) load();
    return () => {
      active = false;
    };
  }, [salonId, getServicePackages, seededPackages]);

  const handleSubmit = () => {
    if (!selectedPackageId) return;
    // The modal closes on success; guard against a double submit meanwhile.
    setIsSubmitting(true);
    onSubmit({ service_packages: [{ service_package_id: selectedPackageId }] });
  };

  return (
    <PackageSpecialPicker
      packages={packages}
      isLoading={isLoading}
      selectedPackageId={selectedPackageId}
      onSelectPackage={setSelectedPackageId}
      onSubmit={handleSubmit}
      submitLabel="Add to Transaction"
      isSubmitting={isSubmitting}
      error={error}
      onClearError={() => setError(null)}
      timezone={timezone}
    />
  );
}
